import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import API from "@src/API.js";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import UserData from "../Controls/UserData";
import { toast } from "react-toastify";

const LEVEL_NAMES = {
  1: "Beginner",
  2: "Junior",
  3: "Intermediate",
  4: "Senior",
  5: "Guru",
};

const emptyForm = () => ({
  skillID: 0,
  skillLevelID: 1,
  questionText: "",
  answers: [
    { text: "", correctAnswer: true },
    { text: "", correctAnswer: false },
  ],
});

export default function SkillQuestionEdit() {
  const [params] = useSearchParams();
  const userData = UserData.UserData() || {};
  const personSkills = userData.personSkills || [];
  const [skillId, setSkillId] = useState(() => Number(params.get("skillId")) || personSkills[0]?.SkillID || 0);
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState(() => ({ ...emptyForm(), skillID: skillId }));
  const [busy, setBusy] = useState(false);

  async function refresh() {
    if (!skillId) {
      setQuestions([]);
      return;
    }
    const resp = await API.getAsync(`/Skill/Question?SkillID=${skillId}`);
    if (resp?.ok) setQuestions(resp.jsonData || []);
  }

  useEffect(() => {
    setForm((f) => ({ ...f, skillID: skillId }));
    refresh();
  }, [skillId]);

  async function onCreate(e) {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        skillID: skillId,
        answers: form.answers.filter((a) => (a.text || "").trim()),
      };
      const resp = await API.postAsync("/Skill/Question", payload);
      if (resp?.ok) {
        toast.success("Question submitted (Pending)");
        setForm({ ...emptyForm(), skillID: skillId });
        await refresh();
      } else {
        toast.error(resp?.jsonData?.message || "Create failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onApprove(id) {
    const resp = await API.postAsync(`/Skill/Question/${id}/Approve`, {});
    if (resp?.ok) {
      toast.success(resp.jsonData?.status === "Approved" ? "Approved" : "Vote recorded");
      await refresh();
    } else {
      toast.error(resp?.jsonData?.message || "Approve failed");
    }
  }

  function updateAnswer(idx, patch) {
    setForm((f) => {
      const answers = f.answers.map((a, i) => (i === idx ? { ...a, ...patch } : a));
      if (patch.correctAnswer === true) {
        answers.forEach((a, i) => {
          if (i !== idx) a.correctAnswer = false;
        });
      }
      return { ...f, answers };
    });
  }

  return (
    <div className="pagebody">
      <BreadCrumb
        props={[
          { route: "/skills", label: "Skills" },
          { route: "/skillTests", label: "Skill Tests" },
          { label: "Questions" },
        ]}
      />
      <div className="tabbody">
        <h1>Skill questions</h1>
        <p>
          Pending questions need 3 approvals from people who hold the skill.{" "}
          <Link to={`/skillTests${skillId ? `?skillId=${skillId}` : ""}`}>Back to tests</Link>
        </p>

        <label>
          Skill{" "}
          <select value={skillId || ""} onChange={(e) => setSkillId(Number(e.target.value) || 0)}>
            <option value="">Select</option>
            {personSkills.map((ps) => (
              <option key={ps.ID} value={ps.SkillID}>
                {ps.SkillName || `Skill ${ps.SkillID}`}
              </option>
            ))}
          </select>
        </label>

        <h2>Create question</h2>
        <form onSubmit={onCreate}>
          <label>
            Level{" "}
            <select
              value={form.skillLevelID}
              onChange={(e) => setForm({ ...form, skillLevelID: Number(e.target.value) })}
            >
              {Object.entries(LEVEL_NAMES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <div>
            <label>
              Question
              <textarea
                value={form.questionText}
                onChange={(e) => setForm({ ...form, questionText: e.target.value })}
                rows={3}
                required
              />
            </label>
          </div>
          {form.answers.map((a, idx) => (
            <div key={idx}>
              <label>
                Answer {idx + 1}
                <input
                  value={a.text}
                  onChange={(e) => updateAnswer(idx, { text: e.target.value })}
                  required={idx < 2}
                />
              </label>
              <label>
                <input
                  type="radio"
                  name="correct"
                  checked={!!a.correctAnswer}
                  onChange={() => updateAnswer(idx, { correctAnswer: true })}
                />{" "}
                Correct
              </label>
            </div>
          ))}
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            onClick={() =>
              setForm({
                ...form,
                answers: [...form.answers, { text: "", correctAnswer: false }],
              })
            }
          >
            Add answer
          </CustomButton>
          <CustomButton type="submit" variant="contained" color="primary" disabled={busy || !skillId}>
            Submit
          </CustomButton>
        </form>

        <h2>Questions for this skill</h2>
        <ul>
          {questions.map((q) => (
            <li key={q.ID || q.id}>
              <strong>{LEVEL_NAMES[q.SkillLevelID || q.skillLevelID] || "?"}</strong> —{" "}
              {q.QuestionText || q.questionText}{" "}
              <em>({q.Status || q.status || q.SkillQuestionStatus})</em>
              {(q.SkillQuestionStatus === 0 || q.status === "Pending" || q.Status === "Pending") && (
                <CustomButton type="button" variant="contained" color="primary" onClick={() => onApprove(q.ID || q.id)}>
                  Approve
                </CustomButton>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
